import * as React from "react";

/** Where a message sits relative to the visible part of the scroll container. */
export type ScrollTargetPosition = "visible" | "above" | "below";

/** The rendered row of message ``messageSequenceNum``, or null. */
export function findMessageArticle(
  container: HTMLElement | null,
  messageSequenceNum: number,
): HTMLElement | null {
  return (
    container?.querySelector<HTMLElement>(
      `[data-message-sequence-num="${messageSequenceNum}"]`,
    ) ?? null
  );
}

/**
 * Tracks whether message ``messageSequenceNum`` is in view in the scroll
 * container, and if not, which way it lies. Undefined while there is no target
 * or it isn't rendered (e.g. inside a collapsed task section).
 *
 * Measured on every scroll or resize of the container (once per frame) and
 * whenever ``renderKey`` changes, so pass something that changes when rows
 * mount, unmount or move without a scroll or resize (message count, expanded
 * section). The row is looked up
 * afresh each time, so a row that was unmounted and mounted again is followed.
 */
export function useScrollTargetPosition(
  scrollContainerRef: React.RefObject<HTMLElement | null>,
  messageSequenceNum: number | undefined,
  renderKey: unknown,
): ScrollTargetPosition | undefined {
  const [position, setPosition] = React.useState<ScrollTargetPosition>();

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || messageSequenceNum === undefined) {
      setPosition(undefined);
      return;
    }
    const measure = () => {
      const target = findMessageArticle(container, messageSequenceNum);
      if (!target) {
        setPosition(undefined);
        return;
      }
      const row = target.getBoundingClientRect();
      const view = container.getBoundingClientRect();
      if (row.bottom <= view.top) {
        setPosition("above");
      } else if (row.top >= view.bottom) {
        setPosition("below");
      } else {
        setPosition("visible");
      }
    };
    let frame: number | undefined;
    const onScroll = () => {
      if (frame === undefined) {
        frame = window.requestAnimationFrame(() => {
          frame = undefined;
          measure();
        });
      }
    };
    measure();
    container.addEventListener("scroll", onScroll, { passive: true });
    // A resize can move the row in or out of view without a scroll.
    const resizeObserver = new ResizeObserver(onScroll);
    resizeObserver.observe(container);
    return () => {
      container.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [scrollContainerRef, messageSequenceNum, renderKey]);

  return position;
}
